/** WalkthroughPlayer — video-style animated product tour (no video asset).
 *  4 auto-advancing scenes with play/pause, progress bar and scene chips.
 *  Swap in a real YouTube/MP4 embed later by replacing <SceneStage/>. */
import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Play, Pause, Camera, ThermometerSun, Landmark, ScanLine,
  CheckCircle2, PhoneCall, FileDown, Sun, Volume2, VolumeX,
  GraduationCap, BadgeCheck, Maximize, Minimize,
} from "lucide-react";

const SCENES = [
  { key: "quote", title: "Intake → Instant Quote", icon: Camera, dur: 7000 },
  { key: "heat", title: "Heat Safety Command", icon: ThermometerSun, dur: 7000 },
  { key: "payroll", title: "50-State Payroll", icon: Landmark, dur: 7000 },
  { key: "scanner", title: "AI Camera Scanners", icon: ScanLine, dur: 7000 },
  { key: "academy", title: "Rep Academy", icon: GraduationCap, dur: 7000 },
];

/* Real screen-capture walkthrough (recorded in-app). Chapter start times in
 * seconds — measured during recording. Falls back to the animated tour when
 * the file isn't present. */
const VIDEO_SRC = "/videos/walkthrough.mp4";
const VIDEO_POSTER = "/videos/walkthrough-poster.jpg";
const VIDEO_CHAPTERS = { quote: 0, heat: 15.7, payroll: 26.4, scanner: 35.1, academy: 44.8 };
const VIDEO_END = 60.5;

const fade = (d = 0) => ({
  initial: { opacity: 0, y: 14 },
  animate: { opacity: 1, y: 0, transition: { delay: d, duration: 0.45 } },
});

const Chip = ({ children, tone = "cyan", d = 0 }) => (
  <motion.span {...fade(d)}
    className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider border
      ${tone === "red" ? "bg-rose-500/15 border-rose-500/40 text-rose-300"
      : tone === "green" ? "bg-emerald-500/15 border-emerald-500/40 text-emerald-300"
      : tone === "amber" ? "bg-amber-500/15 border-amber-500/40 text-amber-300"
      : "bg-cyan-500/15 border-cyan-500/40 text-cyan-300"}`}>
    {children}
  </motion.span>
);

const Row = ({ label, value, d, tone = "text-white" }) => (
  <motion.div {...fade(d)} className="flex items-center justify-between text-xs border-b border-white/5 py-1.5">
    <span className="text-neutral-500">{label}</span>
    <span className={`font-semibold tabular-nums ${tone}`}>{value}</span>
  </motion.div>
);

function QuoteScene() {
  return (
    <div className="grid sm:grid-cols-2 gap-4 md:gap-6 w-full max-w-2xl">
      <div className="rounded-lg border border-white/10 bg-[#141414] p-4">
        <div className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 mb-3">Customer intake — 2 min on their phone</div>
        <Row label="Package" value="Full Detail — from $200" d={0.2} />
        <Row label="Vehicle" value="2021 4Runner · SUV" d={0.5} />
        <Row label="Pet hair" value="Heavy" d={0.8} tone="text-amber-300" />
        <Row label="Photos" value="6 uploaded" d={1.1} />
        <motion.div {...fade(1.5)} className="mt-3 rounded bg-blue-600 text-white text-center text-xs font-bold py-2">Submit request</motion.div>
      </div>
      <div className="flex flex-col justify-center">
        <motion.div initial={{ opacity: 0, scale: 0.85 }} animate={{ opacity: 1, scale: 1, transition: { delay: 2.2, type: "spring" } }}
          className="rounded-lg border border-cyan-500/40 bg-cyan-500/5 p-5">
          <div className="text-[10px] uppercase tracking-[0.2em] text-cyan-300">Instant estimate</div>
          <div className="font-heading text-4xl font-black mt-1 tabular-nums text-white">$292–$340</div>
          <div className="text-[11px] text-neutral-400 mt-1">Pulled 100% from YOUR pricing guide — size, add-ons, travel included</div>
        </motion.div>
        <div className="mt-3 flex gap-2 flex-wrap">
          <Chip d={3.2} tone="green"><CheckCircle2 size={11} /> Photos to you for review</Chip>
          <Chip d={3.8}>Final quote → customer approves</Chip>
        </div>
      </div>
    </div>
  );
}

function HeatScene() {
  return (
    <div className="grid sm:grid-cols-2 gap-4 md:gap-6 w-full max-w-2xl">
      <div className="rounded-lg border border-white/10 bg-[#141414] p-4">
        <div className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 mb-2">Tomorrow · Full Detail · 4h outdoor</div>
        <Row label="Forecast high" value="94°F" d={0.2} />
        <Row label="Humidity" value="68%" d={0.5} />
        <Row label="Heat index" value="108°F" d={0.8} tone="text-orange-300" />
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1, transition: { delay: 1.3 } }} className="mt-3">
          <motion.span animate={{ scale: [1, 1.06, 1] }} transition={{ repeat: Infinity, duration: 1.4, delay: 1.3 }}
            className="inline-flex rounded-full px-3 py-1.5 text-xs font-black bg-rose-500/20 border border-rose-500/50 text-rose-300">
            HEAT RISK: VERY HIGH
          </motion.span>
        </motion.div>
      </div>
      <div className="flex flex-col justify-center gap-2">
        <Chip d={2.0} tone="amber"><Sun size={11} /> Cooler slot: 8–12 AM (−12°F)</Chip>
        <Chip d={2.6} tone="green"><CheckCircle2 size={11} /> Crew check-in: "I'm OK"</Chip>
        <Chip d={3.2} tone="red"><PhoneCall size={11} /> Symptoms → guided 911 flow</Chip>
        <motion.div {...fade(3.9)} className="text-[11px] text-neutral-400">
          Hydration supplies auto-forecast · safety time never touches performance scores
        </motion.div>
      </div>
    </div>
  );
}

function PayrollScene() {
  return (
    <div className="w-full max-w-xl">
      <div className="rounded-lg border border-white/10 bg-[#141414] p-5">
        <div className="flex items-center justify-between mb-3">
          <div className="text-[10px] uppercase tracking-[0.2em] text-neutral-500">Running payroll — all 50 states</div>
          <div className="flex gap-1.5"><Chip d={0.3} tone="green">W-2</Chip><Chip d={0.6}>1099</Chip></div>
        </div>
        <div className="h-1.5 rounded bg-white/10 overflow-hidden mb-4">
          <motion.div initial={{ width: "0%" }} animate={{ width: "100%", transition: { delay: 0.4, duration: 2.2, ease: "easeInOut" } }}
            className="h-full bg-emerald-400" />
        </div>
        <Row label="Alice M. — 38.5h + tips" value="$1,142.60 net" d={2.4} tone="text-emerald-300" />
        <Row label="Marcus S. — 1099 contractor" value="$2,010.00" d={2.9} tone="text-emerald-300" />
        <Row label="Tip credit + OT + PTO" value="auto-applied" d={3.4} />
        <motion.div {...fade(4.2)} className="mt-3 flex items-center gap-2 text-xs text-cyan-300">
          <FileDown size={13} /> Year-end W-2s & 1099s + I-9 vault — one tap
        </motion.div>
      </div>
    </div>
  );
}

function ScannerScene() {
  return (
    <div className="grid sm:grid-cols-2 gap-4 md:gap-6 w-full max-w-2xl">
      <div className="relative rounded-lg border border-white/10 bg-[#141414] p-4 overflow-hidden">
        <div className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 mb-2">Snap the receipt / case</div>
        {["COSTCO WHOLESALE", "KIRKLAND WATER 40CT ... 7.99", "GATORADE 28OZ 12CT ... 14.49", "ICE 10LB ................ 2.49"].map((l, i) => (
          <div key={i} className="font-mono text-[11px] text-neutral-400 py-0.5">{l}</div>
        ))}
        <motion.div initial={{ top: "0%" }} animate={{ top: "100%", transition: { delay: 0.4, duration: 2.0, repeat: 1, ease: "linear" } }}
          className="absolute left-0 right-0 h-0.5 bg-cyan-400 shadow-[0_0_16px_2px_rgba(6,182,212,0.8)]" />
      </div>
      <div className="flex flex-col justify-center">
        <Row label="Kirkland Water 16.9oz" value="+40 @ $0.20" d={2.6} tone="text-emerald-300" />
        <Row label="Gatorade 28oz" value="+12 @ $1.21" d={3.1} tone="text-emerald-300" />
        <Row label="Ice 10lb" value="+1 @ $2.49" d={3.6} tone="text-emerald-300" />
        <div className="mt-3 flex gap-2 flex-wrap">
          <Chip d={4.2} tone="green"><CheckCircle2 size={11} /> Inventory stocked</Chip>
          <Chip d={4.7}>Restock list auto-builds weekly</Chip>
        </div>
      </div>
    </div>
  );
}

function AcademyScene() {
  return (
    <div className="grid sm:grid-cols-2 gap-4 md:gap-6 w-full max-w-2xl">
      <div className="rounded-lg border border-[#c9a227]/50 bg-[#141414] p-4 text-center">
        <div className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 mb-2">Rep Academy — certification</div>
        <motion.div initial={{ opacity: 0, scale: 0.85 }} animate={{ opacity: 1, scale: 1, transition: { delay: 0.4, type: "spring" } }}>
          <div className="font-heading text-xl font-black text-white">Certified Detailing Sales Rep</div>
          <div className="text-[11px] text-neutral-400 mt-1">Marcus S. · Final exam 96%</div>
          <span className="mt-2 inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[10px] font-black bg-emerald-500/15 border border-emerald-500/40 text-emerald-300">
            <BadgeCheck size={11} /> VERIFIED · CERT-8A21F4
          </span>
        </motion.div>
      </div>
      <div className="flex flex-col justify-center gap-2">
        <Chip d={1.4}>AI drafts full courses from your product docs</Chip>
        <Chip d={2.0} tone="green"><CheckCircle2 size={11} /> PDF certificate + public verify link</Chip>
        <Chip d={2.6} tone="amber">Recruit alert: new cert in your territory</Chip>
        <motion.div {...fade(3.3)} className="text-[11px] text-neutral-400">
          Sell training free, one-time, installments, or subscription — marketplace built in.
        </motion.div>
      </div>
    </div>
  );
}

const STAGES = [QuoteScene, HeatScene, PayrollScene, ScannerScene, AcademyScene];

/* ------------------------------------------------------------------ */
/* Real-video mode — plays the recorded screen capture with chapter    */
/* chips + segmented progress, same shell as the animated fallback.    */
/* ------------------------------------------------------------------ */
const chapterBounds = (i) => {
  const keys = SCENES.map((s) => s.key);
  const start = VIDEO_CHAPTERS[keys[i]];
  const end = i + 1 < keys.length ? VIDEO_CHAPTERS[keys[i + 1]] : VIDEO_END;
  return [start, end];
};

function VideoMode() {
  const ref = useRef(null);
  const wrapRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [time, setTime] = useState(0);
  const [started, setStarted] = useState(false);
  const [muted, setMuted] = useState(true);
  const [ended, setEnded] = useState(false);
  const [fs, setFs] = useState(false);

  useEffect(() => {
    const h = () => setFs(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", h);
    return () => document.removeEventListener("fullscreenchange", h);
  }, []);

  const toggleFullscreen = () => {
    const el = wrapRef.current;
    const v = ref.current;
    if (document.fullscreenElement) {
      document.exitFullscreen?.();
    } else if (el?.requestFullscreen) {
      el.requestFullscreen();
    } else if (v?.webkitEnterFullscreen) {
      v.webkitEnterFullscreen(); // iOS Safari
    }
  };

  const keys = SCENES.map((s) => s.key);
  let active = 0;
  for (let i = 0; i < keys.length; i++) if (time >= VIDEO_CHAPTERS[keys[i]] - 0.15) active = i;

  const toggle = () => {
    const v = ref.current;
    if (!v) return;
    setEnded(false);
    if (v.paused) { v.play(); } else { v.pause(); }
  };
  const seek = (i) => {
    const v = ref.current;
    if (!v) return;
    setEnded(false);
    v.currentTime = chapterBounds(i)[0];
    v.play();
  };
  const toggleMute = () => {
    const v = ref.current;
    if (!v) return;
    v.muted = !v.muted;
    setMuted(v.muted);
  };

  const chips = SCENES.map((s, i) => {
    const I = s.icon;
    return (
      <button key={s.key} onClick={() => seek(i)}
        className={`inline-flex items-center gap-1.5 rounded-full px-2.5 md:px-3 py-1.5 text-[10px] md:text-[11px] font-semibold border transition-colors
          ${i === active && started ? "bg-cyan-500/20 border-cyan-400/60 text-cyan-200" : "bg-white/5 border-white/15 text-neutral-300 hover:border-white/40"}`}
        data-testid={`walkthrough-chip-${s.key}`}>
        <I size={12} /> {s.title}
      </button>
    );
  });

  return (
    <>
    <div ref={wrapRef} className={`ld-force-dark relative w-full rounded-xl border border-white/10 bg-[#0A0A0A] overflow-hidden shadow-[0_0_60px_rgba(37,99,235,0.18)] ${fs ? "h-full rounded-none border-0" : "aspect-video"}`}>
      <video
        ref={ref}
        poster={VIDEO_POSTER}
        muted
        playsInline
        preload="metadata"
        className="absolute inset-0 w-full h-full object-contain bg-black"
        onPlay={() => { setPlaying(true); setStarted(true); setEnded(false); }}
        onPause={() => setPlaying(false)}
        onTimeUpdate={(e) => setTime(e.target.currentTime)}
        onEnded={() => { setPlaying(false); setEnded(true); }}
        onClick={toggle}
        data-testid="walkthrough-video"
      >
        <source src={VIDEO_SRC} type="video/mp4" />
        <source src="/videos/walkthrough.webm" type="video/webm" />
      </video>
      {!started && (
        <button onClick={toggle}
          className="absolute inset-0 z-10 flex items-center justify-center group"
          data-testid="walkthrough-video-bigplay">
          <span className="w-16 h-16 md:w-20 md:h-20 rounded-full bg-white/95 text-black flex items-center justify-center transition-transform group-hover:scale-110 shadow-[0_0_40px_rgba(255,255,255,0.35)]">
            <Play size={28} className="ml-1" />
          </span>
        </button>
      )}
      {ended && (
        <div className="absolute inset-0 z-30 flex flex-col items-center justify-center gap-4 bg-black/80" data-testid="walkthrough-endscreen">
          <div className="font-heading text-xl md:text-3xl font-black text-white text-center px-6">
            That's a real day in Legacy IO.<br />Yours starts free.
          </div>
          <a href="/register"
            className="rounded-full bg-[var(--ld-royal,#2563EB)] hover:bg-[var(--ld-royal-hover,#3B82F6)] text-white font-bold text-sm md:text-base px-6 py-3 transition-colors"
            data-testid="walkthrough-end-cta">
            Start free today →
          </a>
          <button onClick={() => seek(0)} className="text-[11px] text-neutral-400 underline" data-testid="walkthrough-replay">
            Replay walkthrough
          </button>
        </div>
      )}
      <div className="absolute bottom-0 left-0 right-0 p-3 md:p-4 bg-gradient-to-t from-black/95 via-black/60 to-transparent z-20">
        <div className="flex gap-1 mb-2.5">
          {SCENES.map((s, i) => {
            const [st, en] = chapterBounds(i);
            const p = time <= st ? 0 : time >= en ? 1 : (time - st) / (en - st);
            return (
              <button key={s.key} onClick={() => seek(i)} aria-label={s.title}
                className="h-1 flex-1 rounded-full bg-white/15 overflow-hidden"
                data-testid={`walkthrough-progress-${s.key}`}>
                <div className="h-full bg-cyan-400" style={{ width: `${p * 100}%` }} />
              </button>
            );
          })}
        </div>
        <div className="flex items-center gap-2 md:gap-3">
          <button onClick={toggle}
            className="w-9 h-9 rounded-full bg-white text-black flex items-center justify-center hover:bg-cyan-300 transition-colors shrink-0"
            data-testid="walkthrough-play-btn">
            {playing ? <Pause size={15} /> : <Play size={15} className="ml-0.5" />}
          </button>
          <button onClick={toggleMute}
            className="w-9 h-9 rounded-full bg-white/10 border border-white/20 text-white flex items-center justify-center hover:bg-white/20 transition-colors shrink-0"
            data-testid="walkthrough-mute-btn" aria-label={muted ? "Unmute" : "Mute"}>
            {muted ? <VolumeX size={15} /> : <Volume2 size={15} />}
          </button>
          <button onClick={toggleFullscreen}
            className="w-9 h-9 rounded-full bg-white/10 border border-white/20 text-white flex items-center justify-center hover:bg-white/20 transition-colors shrink-0"
            data-testid="walkthrough-fullscreen-btn" aria-label={fs ? "Exit fullscreen" : "Fullscreen"}>
            {fs ? <Minimize size={15} /> : <Maximize size={15} />}
          </button>
          {muted && started && (
            <span className="text-[10px] text-neutral-400 sm:hidden">Tap for sound</span>
          )}
          <div className="sm-only-flex gap-1.5 flex-wrap">{chips}</div>
        </div>
      </div>
    </div>
    {/* Mobile: chapter chips live BELOW the frame so they never cover the video */}
    <div className="flex sm:hidden gap-1.5 flex-wrap justify-center mt-3" data-testid="walkthrough-mobile-chips">
      {chips}
    </div>
    </>
  );
}

export const WalkthroughPlayer = () => {
  const [scene, setScene] = useState(0);
  const [playing, setPlaying] = useState(true);
  const [progress, setProgress] = useState(0);
  const [hasVideo, setHasVideo] = useState(null);
  const raf = useRef(null);

  useEffect(() => {
    fetch(VIDEO_SRC, { method: "HEAD" })
      .then((r) => setHasVideo(r.ok && (r.headers.get("content-type") || "").startsWith("video")))
      .catch(() => setHasVideo(false));
  }, []);

  useEffect(() => {
    if (!playing || hasVideo !== false) return;
    const started = Date.now() - progress * SCENES[scene].dur;
    const tick = () => {
      const p = (Date.now() - started) / SCENES[scene].dur;
      if (p >= 1) {
        setScene((s) => (s + 1) % SCENES.length);
        setProgress(0);
      } else {
        setProgress(p);
        raf.current = requestAnimationFrame(tick);
      }
    };
    raf.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playing, scene, hasVideo]);

  const pick = (i) => { setScene(i); setProgress(0); setPlaying(true); };
  const Stage = STAGES[scene];

  if (hasVideo === null) {
    return <div className="w-full max-w-5xl mx-auto"><div className="aspect-video w-full rounded-xl border border-white/10 bg-[#0A0A0A]" data-testid="walkthrough-player" /></div>;
  }
  if (hasVideo) {
    return (
      <div className="w-full max-w-5xl mx-auto" data-testid="walkthrough-player">
        <VideoMode />
        <div className="mt-2 text-center text-[10px] text-neutral-600">
          Real screen capture — recorded inside the OS, no mockups.
        </div>
      </div>
    );
  }

  return (
    <div className="w-full max-w-5xl mx-auto" data-testid="walkthrough-player">
      <div className="ld-force-dark relative aspect-[3/4] sm:aspect-video w-full rounded-xl border border-white/10 bg-[#0A0A0A] overflow-hidden shadow-[0_0_60px_rgba(37,99,235,0.18)]">
        {/* stage */}
        <div className="absolute inset-0 flex items-center justify-center p-4 md:p-10 pb-28 sm:pb-24">
          <AnimatePresence mode="wait">
            <motion.div key={scene} initial={{ opacity: 0, x: 40 }} animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -40 }} transition={{ duration: 0.35 }}
              className="w-full flex justify-center" data-testid={`walkthrough-scene-${SCENES[scene].key}`}>
              <Stage />
            </motion.div>
          </AnimatePresence>
        </div>
        {/* grain + vignette */}
        <div className="absolute inset-0 pointer-events-none bg-[radial-gradient(ellipse_at_center,transparent_55%,rgba(0,0,0,0.55))]" />
        {/* controls */}
        <div className="absolute bottom-0 left-0 right-0 p-3 md:p-4 bg-gradient-to-t from-black/95 via-black/70 to-transparent z-20">
          <div className="flex gap-1 mb-2.5">
            {SCENES.map((s, i) => (
              <button key={s.key} onClick={() => pick(i)} aria-label={s.title}
                className="h-1 flex-1 rounded-full bg-white/15 overflow-hidden"
                data-testid={`walkthrough-progress-${s.key}`}>
                <div className="h-full bg-cyan-400 transition-none"
                  style={{ width: i < scene ? "100%" : i === scene ? `${progress * 100}%` : "0%" }} />
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2 md:gap-3 flex-wrap">
            <button onClick={() => setPlaying((p) => !p)}
              className="w-9 h-9 rounded-full bg-white text-black flex items-center justify-center hover:bg-cyan-300 transition-colors shrink-0"
              data-testid="walkthrough-play-btn">
              {playing ? <Pause size={15} /> : <Play size={15} className="ml-0.5" />}
            </button>
            <div className="flex gap-1.5 flex-wrap">
              {SCENES.map((s, i) => {
                const I = s.icon;
                return (
                  <button key={s.key} onClick={() => pick(i)}
                    className={`inline-flex items-center gap-1.5 rounded-full px-2.5 md:px-3 py-1.5 text-[10px] md:text-[11px] font-semibold border transition-colors
                      ${i === scene ? "bg-cyan-500/20 border-cyan-400/60 text-cyan-200" : "bg-white/5 border-white/15 text-neutral-300 hover:border-white/40"}`}
                    data-testid={`walkthrough-chip-${s.key}`}>
                    <I size={12} /> {s.title}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>
      <div className="mt-2 text-center text-[10px] text-neutral-600">
        Live product tour — every screen above ships in Legacy IO today.
      </div>
    </div>
  );
};

export default WalkthroughPlayer;
